import { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"

import HttpManager from "../../services/HttpManager"
import UnderlineHeading from "../underlineHeading"
import { ScheduleHistoryDTO, ScheduleNoteDTO } from "../../models/ScheduleHistoryDTO"

import "./historialHorario.css"

/*
	Historial y observaciones de un horario. (Esteban, 2026-09-21)

	Tres cosas, en el orden en que se consultan:

	1. OBSERVACIONES. Se agregan, no se editan, y queda quien la puso. Igual que
	   las del alumno.
	2. AULAS. De donde estuvo el horario y cuando. Sale del registro de CLASES, no
	   de la bitacora, asi que cubre todo el pasado -- incluidos los cambios de un
	   solo dia, que aparecen como un tramo de una clase.
	3. PROFESORES. Aqui no hay fuente hacia atras: el horario guarda un solo
	   maestro y las clases no guardan quien las dio. El rastro empieza el dia que
	   se instalo la bitacora, y eso se dice en pantalla para que un tramo sin
	   fecha de inicio no se lea como "empezo ese dia".
	4. La bitacora completa, al final, para quien quiera el detalle.
*/

const soloFecha = (valor : string | null) : string =>
{
	const texto = String(valor || "")

	if(texto === "") return "-"

	const partes = texto.slice(0, 10).split("-")

	return partes.length === 3 ? partes[2] + "/" + partes[1] + "/" + partes[0] : texto
}

const fechaYHora = (valor : string | null) : string =>
{
	const texto = String(valor || "")

	if(texto === "") return "-"

	const hora = texto.length > 11 ? " · " + texto.slice(11, 16) : ""

	return soloFecha(texto) + hora
}

interface Props
{
	campusId   : number
	scheduleId : number
	/** Quien no puede modificar el horario tampoco deja observaciones. */
	puedeEscribir : boolean
}

const HistorialHorario = ({campusId, scheduleId, puedeEscribir} : Props) =>
{
	const http = HttpManager.getInstance()

	const [historial, setHistorial] = useState<ScheduleHistoryDTO | null>(null)
	const [notas, setNotas]         = useState<ScheduleNoteDTO[]>([])
	const [error, setError]         = useState<string | null>(null)
	const [texto, setTexto]         = useState<string>("")
	const [guardando, setGuardando] = useState<boolean>(false)
	const [verBitacora, setVerBitacora] = useState<boolean>(false)

	useEffect
	(
		() =>
		{
			let cancelado = false

			const traer = async () =>
			{
				try
				{
					const [h, n] = await Promise.all
					([
						http.scheduleService.getScheduleHistory(campusId, scheduleId),
						http.scheduleService.getScheduleNotes(campusId, scheduleId)
					])

					if(cancelado) return

					setHistorial(h.data)
					setNotas(n.data || [])
				}
				catch(err : any)
				{
					if(!cancelado) setError("El historial no pudo cargarse.")
					console.error(err)
				}
			}

			traer()

			return () => {cancelado = true}
		},
		[campusId, scheduleId]
	)

	const guardarNota = async () =>
	{
		const limpio = texto.trim()

		if(limpio === "" || guardando) return

		try
		{
			setGuardando(true)

			const respuesta = await http.scheduleService.addScheduleNote(campusId, scheduleId, limpio)

			setNotas(respuesta.data || [])
			setTexto("")
		}
		catch(err : any)
		{
			setError("La observación no pudo guardarse.")
			console.error(err)
		}
		finally
		{
			setGuardando(false)
		}
	}

	return <div className="waHh">
		<UnderlineHeading name={"Observaciones"} />
		{
			!puedeEscribir ? null : <div className="waHhNueva">
				<textarea
					className="waHhCampo"
					placeholder="Agregar nueva observación..."
					value={texto}
					disabled={guardando}
					onChange={(evento) => setTexto(evento.target.value)}
				/>
				<button type="button" className="waHhGuardar" disabled={guardando || texto.trim() === ""} onClick={guardarNota}>
					<FontAwesomeIcon icon={solid("plus")} />
					<span>
						Guardar observación
					</span>
				</button>
			</div>
		}
		{
			notas.length < 1
					?
				<p className="waHhVacio">
					Todavía no hay observaciones.
				</p>
					:
				<ul className="waHhNotas">{
					notas.map((nota) => <li key={nota.id}>
						<span className="waHhFirma">
							{nota.createdBy} · {fechaYHora(nota.createdAt)}
						</span>
						<span className="waHhTexto">
							{nota.note}
						</span>
					</li>)
				}</ul>
		}

		<UnderlineHeading name={"Aulas"} />
		{
			!historial || historial.classroom.length < 1
					?
				<p className="waHhVacio">
					Este horario todavía no tiene clases registradas.
				</p>
					:
				<ul className="waHhTramos">{
					historial.classroom.map((tramo, indice) => <li key={"aula_" + indice}>
						<span className="waHhQue">
							{tramo.classroom || "Sin aula"}
							{!tramo.building ? null : <span className="waHhDonde">
								{tramo.building}
							</span>}
						</span>
						<span className="waHhCuando">
							{soloFecha(tramo.from)} – {soloFecha(tramo.to)}
							<span className="waHhCuantas">
								{tramo.classes === 1 ? "1 clase" : tramo.classes + " clases"}
							</span>
						</span>
					</li>)
				}</ul>
		}
		<p className="waHhNota">
			Sale del registro de clases, así que cubre todo el pasado. Un cambio de un solo
			día aparece como un tramo de una clase.
		</p>

		<UnderlineHeading name={"Profesores"} />
		{
			!historial || historial.mentor.length < 1
					?
				<p className="waHhVacio">
					Sin datos de profesor.
				</p>
					:
				<ul className="waHhTramos">{
					historial.mentor.map((tramo, indice) => <li key={"maestro_" + indice}>
						<span className="waHhQue">
							{tramo.mentor || "Sin profesor"}
						</span>
						<span className="waHhCuando">
							{tramo.unknownStart ? "desde antes" : soloFecha(tramo.from)}
							{" – "}
							{tramo.to ? soloFecha(tramo.to) : "hoy"}
						</span>
					</li>)
				}</ul>
		}
		<p className="waHhNota">
			El rastro de profesores empieza el día que se instaló la bitácora: antes de eso
			WAPP no guardaba los cambios en ningún lado. "Desde antes" quiere decir que ya
			estaba, no que empezó ese día.
		</p>

		<button type="button" className="waHhVerMas" onClick={() => setVerBitacora(!verBitacora)}>
			<FontAwesomeIcon icon={verBitacora ? solid("chevron-up") : solid("chevron-down")} />
			<span>
				{verBitacora ? "Ocultar" : "Ver"} la bitácora completa
				{!historial ? "" : " (" + historial.log.length + ")"}
			</span>
		</button>
		{
			!verBitacora || !historial ? null :
				(
					historial.log.length < 1
							?
						<p className="waHhVacio">
							Todavía no hay cambios registrados.
						</p>
							:
						<ul className="waHhBitacora">{
							historial.log.map((entrada) => <li key={entrada.id}>
								<span className="waHhFirma">
									{entrada.createdBy} · {fechaYHora(entrada.createdAt)}
								</span>
								<span className="waHhTexto">
									{entrada.summary}
								</span>
								{
									entrada.changes.length < 1 ? null : <ul className="waHhCambios">{
										entrada.changes.map((cambio, i) => <li key={entrada.id + "_" + i}>
											<b>{cambio.label}:</b> {cambio.old || "—"} → {cambio.new || "—"}
										</li>)
									}</ul>
								}
							</li>)
						}</ul>
				)
		}

		{!error ? null : <p className="waHhError">{error}</p>}
	</div>
}

export default HistorialHorario
